import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Save,
  Shield,
  Key,
  UserPlus,
  Lock,
  Timer,
  CheckCircle,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { useToast } from '../components/ui/Toast.tsx';
import { extractError } from '../lib/types.ts';

// ─── Types (matches AuthConfig from @map/shared) ────────────────────────────

interface AuthConfig {
  methods: {
    emailPassword: { enabled: boolean };
    google: { enabled: boolean };
    meta: { enabled: boolean };
    phoneOtp: { enabled: boolean };
    magicLink: { enabled: boolean };
  };
  signup: {
    enabled: boolean;
    requireEmailVerification: boolean;
  };
  passwordPolicy: {
    minLength: number;
    maxLength: number;
    requireUppercase: boolean;
    requireDigit: boolean;
    requireSpecialChar: boolean;
  };
  security: {
    maxLoginAttempts: number;
    lockoutDurationMinutes: number;
    otpTtlMinutes: number;
    otpMaxAttempts: number;
    magicLinkTtlMinutes: number;
    resetTokenTtlHours: number;
  };
  tokens: {
    accessTokenTtlSeconds: number;
    refreshTokenTtlDays: number;
  };
}

const DEFAULT_AUTH_CONFIG: AuthConfig = {
  methods: {
    emailPassword: { enabled: true },
    google: { enabled: false },
    meta: { enabled: false },
    phoneOtp: { enabled: false },
    magicLink: { enabled: false },
  },
  signup: {
    enabled: true,
    requireEmailVerification: false,
  },
  passwordPolicy: {
    minLength: 8,
    maxLength: 128,
    requireUppercase: false,
    requireDigit: false,
    requireSpecialChar: false,
  },
  security: {
    maxLoginAttempts: 10,
    lockoutDurationMinutes: 15,
    otpTtlMinutes: 5,
    otpMaxAttempts: 3,
    magicLinkTtlMinutes: 15,
    resetTokenTtlHours: 1,
  },
  tokens: {
    accessTokenTtlSeconds: 900,
    refreshTokenTtlDays: 30,
  },
};

// ─── Collapsible Section ─────────────────────────────────────────────────────

function Section({
  title,
  icon,
  description,
  defaultOpen = true,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  description: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-zinc-50 transition-colors"
      >
        <div className="w-8 h-8 rounded-lg bg-zinc-100 flex items-center justify-center text-zinc-600 shrink-0">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-zinc-900">{title}</h3>
          <p className="text-xs text-zinc-500 mt-0.5">{description}</p>
        </div>
        {open ? (
          <ChevronDown size={16} className="text-zinc-400 shrink-0" />
        ) : (
          <ChevronRight size={16} className="text-zinc-400 shrink-0" />
        )}
      </button>
      {open && <div className="px-5 pb-5 border-t border-zinc-100 pt-4">{children}</div>}
    </div>
  );
}

// ─── Toggle row ──────────────────────────────────────────────────────────────

function ToggleRow({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between py-2.5 cursor-pointer group">
      <div className="min-w-0 mr-4">
        <span className="text-sm text-zinc-800 font-medium">{label}</span>
        {description && <p className="text-xs text-zinc-400 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 ${
          checked ? 'bg-zinc-900' : 'bg-zinc-200'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </label>
  );
}

// ─── Number field ────────────────────────────────────────────────────────────

function NumberField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  suffix,
}: {
  label: string;
  hint?: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  suffix?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-zinc-700">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => {
            const n = parseInt(e.target.value, 10);
            if (!isNaN(n)) onChange(n);
          }}
          min={min}
          max={max}
          className="w-28 h-9 px-3 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-900 focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent transition-shadow"
        />
        {suffix && <span className="text-xs text-zinc-400">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-zinc-400">{hint}</p>}
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function TenantAuthSettingsTab() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  // Load auth_config from integrations
  const { data: rawConfig, isLoading } = useQuery<AuthConfig>({
    queryKey: ['integrations', 'auth_config'],
    queryFn: async () => {
      const res = await api.get('/api/v1/integrations');
      return res.data?.auth_config ?? null;
    },
    retry: false,
  });

  const [config, setConfig] = useState<AuthConfig>(DEFAULT_AUTH_CONFIG);
  const [dirty, setDirty] = useState(false);

  // Sync remote data into local state
  useEffect(() => {
    if (rawConfig) {
      setConfig(mergeDeep(DEFAULT_AUTH_CONFIG, rawConfig as Partial<AuthConfig>));
      setDirty(false);
    }
  }, [rawConfig]);

  // Mutation
  const saveMutation = useMutation({
    mutationFn: () => api.patch('/api/v1/integrations/auth_config', config),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      toast.success('Authentication settings saved');
      setDirty(false);
    },
    onError: (err) => toast.error(extractError(err, 'Error saving auth settings')),
  });

  // Deep updater
  function update<K extends keyof AuthConfig>(
    section: K,
    field: string,
    value: unknown,
  ) {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev[section], [field]: value },
    }));
    setDirty(true);
  }

  function updateMethod(method: keyof AuthConfig['methods'], enabled: boolean) {
    setConfig((prev) => ({
      ...prev,
      methods: {
        ...prev.methods,
        [method]: { enabled },
      },
    }));
    setDirty(true);
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 size={24} className="animate-spin text-zinc-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          type="button"
          onClick={() => navigate('/integrations')}
          className="w-8 h-8 rounded-lg bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-zinc-600 transition-colors"
        >
          <ArrowLeft size={16} />
        </button>
        <PageHeader
          title="Authentication Settings"
          description="Configure login methods, password policies, security limits, and token lifetimes"
        />
      </div>

      <div className="space-y-4">
        {/* ─── Section A: Authentication Methods ─── */}
        <Section
          title="Authentication Methods"
          icon={<Key size={16} />}
          description="Enable or disable login methods available to customers"
        >
          <div className="divide-y divide-zinc-100">
            <ToggleRow
              label="Email + Password"
              description="Standard email and password login"
              checked={config.methods.emailPassword.enabled}
              onChange={(v) => updateMethod('emailPassword', v)}
            />
            <ToggleRow
              label="Google SSO"
              description="Sign in with Google account (requires OAuth config)"
              checked={config.methods.google.enabled}
              onChange={(v) => updateMethod('google', v)}
            />
            <ToggleRow
              label="Meta (Facebook) SSO"
              description="Sign in with Facebook account (requires OAuth config)"
              checked={config.methods.meta.enabled}
              onChange={(v) => updateMethod('meta', v)}
            />
            <ToggleRow
              label="Phone OTP"
              description="One-time code sent via SMS"
              checked={config.methods.phoneOtp.enabled}
              onChange={(v) => updateMethod('phoneOtp', v)}
            />
            <ToggleRow
              label="Magic Link (email)"
              description="Passwordless login via email link"
              checked={config.methods.magicLink.enabled}
              onChange={(v) => updateMethod('magicLink', v)}
            />
          </div>
        </Section>

        {/* ─── Section B: Signup Settings ─── */}
        <Section
          title="Signup Settings"
          icon={<UserPlus size={16} />}
          description="Control self-registration and verification requirements"
        >
          <div className="divide-y divide-zinc-100">
            <ToggleRow
              label="Allow self-registration"
              description="Customers can create accounts on their own"
              checked={config.signup.enabled}
              onChange={(v) => update('signup', 'enabled', v)}
            />
            <ToggleRow
              label="Require email verification"
              description="New accounts must verify their email before accessing features"
              checked={config.signup.requireEmailVerification}
              onChange={(v) => update('signup', 'requireEmailVerification', v)}
            />
          </div>
        </Section>

        {/* ─── Section C: Password Policy ─── */}
        <Section
          title="Password Policy"
          icon={<Lock size={16} />}
          description="Set minimum requirements for customer passwords"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <NumberField
                label="Minimum length"
                value={config.passwordPolicy.minLength}
                onChange={(v) => update('passwordPolicy', 'minLength', v)}
                min={6}
                max={128}
                suffix="characters"
              />
              <NumberField
                label="Maximum length"
                value={config.passwordPolicy.maxLength}
                onChange={(v) => update('passwordPolicy', 'maxLength', v)}
                min={6}
                max={128}
                suffix="characters"
              />
            </div>
            <div className="divide-y divide-zinc-100">
              <ToggleRow
                label="Require uppercase letter"
                checked={config.passwordPolicy.requireUppercase}
                onChange={(v) => update('passwordPolicy', 'requireUppercase', v)}
              />
              <ToggleRow
                label="Require digit"
                checked={config.passwordPolicy.requireDigit}
                onChange={(v) => update('passwordPolicy', 'requireDigit', v)}
              />
              <ToggleRow
                label="Require special character"
                description="e.g. !@#$%^&*()"
                checked={config.passwordPolicy.requireSpecialChar}
                onChange={(v) => update('passwordPolicy', 'requireSpecialChar', v)}
              />
            </div>
          </div>
        </Section>

        {/* ─── Section D: Security ─── */}
        <Section
          title="Security"
          icon={<Shield size={16} />}
          description="Rate limiting, lockout, and OTP settings"
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <NumberField
              label="Max login attempts before lockout"
              hint="Set to 0 to disable lockout"
              value={config.security.maxLoginAttempts}
              onChange={(v) => update('security', 'maxLoginAttempts', v)}
              min={0}
            />
            <NumberField
              label="Lockout duration"
              value={config.security.lockoutDurationMinutes}
              onChange={(v) => update('security', 'lockoutDurationMinutes', v)}
              min={1}
              suffix="minutes"
            />
            <NumberField
              label="OTP TTL"
              value={config.security.otpTtlMinutes}
              onChange={(v) => update('security', 'otpTtlMinutes', v)}
              min={1}
              max={30}
              suffix="minutes"
            />
            <NumberField
              label="OTP max attempts"
              value={config.security.otpMaxAttempts}
              onChange={(v) => update('security', 'otpMaxAttempts', v)}
              min={1}
              max={10}
            />
            <NumberField
              label="Magic link TTL"
              value={config.security.magicLinkTtlMinutes}
              onChange={(v) => update('security', 'magicLinkTtlMinutes', v)}
              min={1}
              max={60}
              suffix="minutes"
            />
            <NumberField
              label="Password reset token TTL"
              value={config.security.resetTokenTtlHours}
              onChange={(v) => update('security', 'resetTokenTtlHours', v)}
              min={1}
              max={72}
              suffix="hours"
            />
          </div>
        </Section>

        {/* ─── Section E: Token TTLs ─── */}
        <Section
          title="Token Lifetimes"
          icon={<Timer size={16} />}
          description="JWT access and refresh token expiration"
          defaultOpen={false}
        >
          <div className="grid grid-cols-2 gap-x-6 gap-y-4">
            <NumberField
              label="Access token TTL"
              hint="Short-lived JWT (default: 900 = 15 min)"
              value={config.tokens.accessTokenTtlSeconds}
              onChange={(v) => update('tokens', 'accessTokenTtlSeconds', v)}
              min={60}
              max={86400}
              suffix="seconds"
            />
            <NumberField
              label="Refresh token TTL"
              hint="Long-lived refresh token (default: 30 days)"
              value={config.tokens.refreshTokenTtlDays}
              onChange={(v) => update('tokens', 'refreshTokenTtlDays', v)}
              min={1}
              max={365}
              suffix="days"
            />
          </div>
        </Section>
      </div>

      {/* ─── Sticky save bar ─── */}
      <div className="sticky bottom-0 bg-white/80 backdrop-blur-sm border-t border-zinc-200 -mx-6 px-6 py-3 mt-6 flex items-center justify-between">
        <div className="text-xs text-zinc-500">
          {dirty ? (
            <span className="flex items-center gap-1.5 text-amber-600">
              <AlertCircle size={12} />
              Unsaved changes
            </span>
          ) : (
            <span className="flex items-center gap-1.5 text-zinc-400">
              <CheckCircle size={12} />
              All changes saved
            </span>
          )}
        </div>
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={!dirty || saveMutation.isPending}
        >
          {saveMutation.isPending ? (
            <Loader2 size={14} className="animate-spin mr-1.5" />
          ) : (
            <Save size={14} className="mr-1.5" />
          )}
          Save Settings
        </Button>
      </div>
    </div>
  );
}

// ─── Deep merge helper ───────────────────────────────────────────────────────

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mergeDeep<T>(defaults: T, override: Partial<T>): T {
  const result = { ...defaults } as any;
  const ov = override as any;
  for (const key of Object.keys(defaults as any)) {
    if (
      ov[key] !== undefined &&
      typeof (defaults as any)[key] === 'object' &&
      (defaults as any)[key] !== null &&
      typeof ov[key] === 'object' &&
      ov[key] !== null &&
      !Array.isArray((defaults as any)[key])
    ) {
      result[key] = mergeDeep((defaults as any)[key], ov[key]);
    } else if (ov[key] !== undefined) {
      result[key] = ov[key];
    }
  }
  return result as T;
}
